import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Avatar from '../ui/Avatar';
import Icon, { IconName } from '../ui/Icon';
import Text from '../ui/Text';
import { colors, radii, screenPadding, spacing, useResponsive } from '../../theme';
import { useVoiceCall } from '../../voice/VoiceCallProvider';
import {
  formatDuration,
  isLivePhase,
  nameOf,
  phaseCopy,
  type CallPhase,
} from '../../voice/types';

/**
 * The call itself - dialling, ringing, talking, and how it ended.
 *
 * A `Modal` for the same reason `IncomingCallOverlay` is one: there is no
 * navigator in this app to push a call screen onto, and a Modal mounted at the
 * root floats over whichever screen `HomeScreen`'s guard chain happened to
 * return. See that file's header for the longer version.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN IS NOT ALLOWED TO SAY
 * ---------------------------------------------------------------------------
 * It never claims a state the server has not confirmed, and it never claims
 * audio the engine has not reported. Those are two separate facts and this
 * draws them separately:
 *
 *   "Ringing…"  the server says the call is RINGING
 *   "Connected" the server says ACCEPTED *and* the engine says connected
 *
 * With no audio engine installed - which is the state of this app until the
 * Synquic Voice SDK is delivered, see `src/voice/transport.ts` - the second
 * one can never be true, and this screen says so in as many words instead of
 * drawing a call that is silently doing nothing. A customer saying "hello"
 * into a dead line while the screen tells them everything is fine is the worst
 * outcome available here, and it is the one this avoids.
 */

/** The phases this overlay owns. `incoming` belongs to the other one. */
const SHOWN: CallPhase[] = [
  'requesting-permission',
  'dialling',
  'ringing-out',
  'connecting',
  'connected',
  'ending',
  'ended',
];

const ACTION = 62;
const END = 68;

/**
 * Seconds since the call was answered, ticking.
 *
 * Measured from the server's `acceptedAt` rather than from a local start
 * moment, so the number matches the talk time the platform will bill and log -
 * the vendor measures duration from acceptance, and a timer started when this
 * component mounted would drift away from it by however long the connection
 * took.
 */
function useElapsed(acceptedAt: string | null | undefined, running: boolean) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!running || !acceptedAt) {
      setSeconds(0);
      return;
    }

    const start = new Date(acceptedAt).getTime();
    if (Number.isNaN(start)) {
      return;
    }

    const tick = () => setSeconds(Math.max(0, (Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [acceptedAt, running]);

  return seconds;
}

function OngoingCallOverlay() {
  const voice = useVoiceCall();
  const insets = useSafeAreaInsets();
  const resp = useResponsive();

  const { phase, call, ended, engineAvailable } = voice;
  const visible = SHOWN.includes(phase);
  const who = nameOf(call);

  const elapsed = useElapsed(call?.acceptedAt, phase === 'connected');

  /* Answered, as far as the server is concerned. Distinct from "audible". */
  const answered = phase === 'connecting' || phase === 'connected';
  const controlsLive = answered && engineAvailable;

  /*
   * The status line.
   *
   * The engine's absence overrides "Connected" rather than sitting beside it.
   * The call really has been answered - the other person is on it - so this
   * says that, and then says the part that is not working, instead of a single
   * word that would be read as everything being fine.
   */
  const status =
    phase === 'ended'
      ? null
      : answered && !engineAvailable
      ? 'Answered - no audio on this device'
      : phaseCopy(phase);

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={false}
      statusBarTranslucent
      onRequestClose={() => {
        /* Back clears the "how it ended" card, and nothing else. A live call
           is hung up with the red button - never by a reflex press, and never
           by walking `HomeScreen`'s own back stack out from under it. */
        if (!isLivePhase(phase)) {
          voice.dismiss();
        }
      }}
    >
      <View
        style={[
          styles.root,
          {
            paddingTop: insets.top + spacing.xxxl,
            paddingBottom: insets.bottom + spacing.xxxl,
            paddingHorizontal: screenPadding + resp.sideInset,
          },
        ]}
      >
        <View style={styles.identity}>
          <Avatar name={who} size={104} tone="inverse" style={styles.avatar} />

          <Text variant="titleLg" color={colors.inkInverse} align="center">
            {who}
          </Text>

          {status ? (
            <Text variant="bodySm" color={colors.inkInverseMuted} align="center">
              {status}
            </Text>
          ) : null}

          {/* The clock, only once there is something to count. */}
          {phase === 'connected' ? (
            <Text variant="title" color={colors.inkInverse}>
              {formatDuration(elapsed)}
            </Text>
          ) : null}

          {/* How it went - "No answer" and "Declined" are deliberately not the
              same sentence. See `endedCopy` in `src/voice/types.ts`. */}
          {phase === 'ended' && ended ? (
            <View style={styles.endedBlock}>
              <Text variant="title" color={colors.inkInverse} align="center">
                {ended.title}
              </Text>
              {ended.detail ? (
                <Text
                  variant="bodySm"
                  color={colors.inkInverseMuted}
                  align="center"
                >
                  {ended.detail}
                </Text>
              ) : null}
            </View>
          ) : null}
        </View>

        {/*
          The missing engine, stated rather than hidden.

          Drawn from the moment the call is placed, not once it is answered, so
          nobody discovers it mid-conversation. Everything else on this call is
          real - it rings the other handset, it can be declined, it times out
          server-side - and the copy is careful to say that this is the audio
          alone.
        */}
        {!engineAvailable && phase !== 'ended' ? (
          <View style={styles.engine}>
            <Icon name="alert" size={18} color={colors.inkInverse} />
            <View style={styles.engineText}>
              <Text variant="labelSm" color={colors.inkInverse}>
                Voice engine not installed
              </Text>
              <Text variant="caption" color={colors.inkInverseMuted}>
                This build has no audio engine, so the call will connect but
                carry no sound. Everything else works.
              </Text>
            </View>
          </View>
        ) : null}

        {voice.notice ? (
          <View style={styles.engine}>
            <Icon name="alert" size={18} color={colors.inkInverse} />
            <Text variant="caption" color={colors.inkInverse} style={styles.engineText}>
              {voice.notice}
            </Text>
          </View>
        ) : null}

        {phase === 'ended' ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={voice.dismiss}
            style={({ pressed }) => [styles.close, pressed ? styles.pressed : null]}
          >
            <Text variant="label" color={colors.inkInverse}>
              Close
            </Text>
          </Pressable>
        ) : (
          <View style={styles.actions}>
            <RoundControl
              icon={voice.muted ? 'micOff' : 'mic'}
              label={voice.muted ? 'Unmute' : 'Mute'}
              active={voice.muted}
              disabled={!controlsLive}
              onPress={voice.toggleMuted}
            />

            <View style={styles.action}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="End call"
                onPress={voice.end}
                disabled={phase === 'ending'}
                style={({ pressed }) => [
                  styles.end,
                  pressed ? styles.pressed : null,
                  phase === 'ending' ? styles.inert : null,
                ]}
              >
                <Icon name="phoneEnd" size={28} color={colors.onDanger} />
              </Pressable>
              <Text variant="labelSm" color={colors.inkInverseMuted}>
                End
              </Text>
            </View>

            <RoundControl
              icon="speaker"
              label="Speaker"
              active={voice.speaker}
              disabled={!controlsLive}
              onPress={voice.toggleSpeaker}
            />
          </View>
        )}
      </View>
    </Modal>
  );
}

/**
 * One of the two toggles either side of the hang-up.
 *
 * `active` inverts the fill rather than tinting the glyph: on a dark ground a
 * colour change on a 22pt icon is not a state anybody can read across a room,
 * and mute in particular has to be obvious at a glance.
 */
function RoundControl({
  icon,
  label,
  active,
  disabled,
  onPress,
}: {
  icon: IconName;
  label: string;
  active: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <View style={styles.action}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ selected: active, disabled }}
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => [
          styles.control,
          active ? styles.controlOn : null,
          pressed ? styles.pressed : null,
          disabled ? styles.inert : null,
        ]}
      >
        <Icon
          name={icon}
          size={22}
          color={active ? colors.ink : colors.inkInverse}
        />
      </Pressable>
      <Text variant="labelSm" color={colors.inkInverseMuted}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: colors.surfaceInverse,
  },
  identity: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  avatar: {
    marginBottom: spacing.lg,
  },
  endedBlock: {
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  engine: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    marginBottom: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.overlay,
  },
  engineText: {
    flex: 1,
    gap: 2,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-evenly',
  },
  action: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  control: {
    width: ACTION,
    height: ACTION,
    borderRadius: ACTION / 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.inkInverseMuted,
  },
  controlOn: {
    backgroundColor: colors.surfaceMuted,
    borderColor: colors.surfaceMuted,
  },
  /* Solid red, which the house `danger` button deliberately is not - see the
     note on the decline circle in `IncomingCallOverlay`. */
  end: {
    width: END,
    height: END,
    borderRadius: END / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.danger,
  },
  close: {
    alignSelf: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xxl,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.inkInverseMuted,
  },
  pressed: {
    opacity: 0.6,
  },
  inert: {
    opacity: 0.45,
  },
});

export default OngoingCallOverlay;
